import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import '../App.css';
import GlobalCollapsibleButton from '../components/GlobalCollapsibleButton';
import { DeckIntelligenceSections } from '../components/DeckIntelligenceSections';
import GlobalEmptyState from '../components/GlobalEmptyState';
import GlobalSidebar from '../components/GlobalSidebar';
import { GlobalButton } from '../components/GlobalButton';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import GlobalTabs from '../components/GlobalTabs';
import { InvestmentCaseChatPanel } from '../components/InvestmentCaseChatPanel';
import { InvestmentCaseForm } from '../components/InvestmentCaseForm';
import { InvestmentCaseScoreRadar } from '../components/InvestmentCaseScoreRadar';
import { useAnalysisJobs } from '../context/AnalysisJobsContext';
import { useAppData } from '../context/AppDataContext';
import { useUser } from '../context/UserContext';
import { useTheme } from '../context/ThemeProvider';
import { deleteInvestmentCaseDeck } from '../lib/investmentCaseDeckStorage';
import {
  extractDeckText,
  extractDeckTextErrorMessage,
} from '../lib/extractDeckText';
import { deckIntelligenceSourceFingerprint } from '../utils/deckIntelligence';
import { Favicon, normalizeWebsiteUrl } from '../utils/favicon';
import { newId } from '../utils/id';
import {
  investmentCaseAverageScore,
  investmentCaseVerdict,
} from '../utils/investmentCase';
import { flagSvgUrlForCountryCode } from '../utils/investmentCompanyUi';
import type { InvestmentCase } from '../types';

const DETAIL_TAB_KEYS = ['details', 'deck', 'founders', 'intelligence', 'chat'] as const;
type DetailTabKey = (typeof DETAIL_TAB_KEYS)[number];

function tabIndexFromSearch(tab: string | null): number {
  if (!tab) return 0;
  const i = DETAIL_TAB_KEYS.indexOf(tab as DetailTabKey);
  return i >= 0 ? i : 0;
}

export function InvestmentCaseDetailView() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUser();
  const userId = user?.uid ?? '';
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { investmentCases, setInvestmentCases } = useAppData();
  const { isAnalyzing, getJobError, startDeckAnalysis } = useAnalysisJobs();

  const [editOpen, setEditOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractFeedback, setExtractFeedback] = useState<{
    kind: 'success' | 'error';
    message: string;
  } | null>(null);

  const investmentCase = useMemo(
    () => investmentCases.find((c) => c.id === caseId),
    [investmentCases, caseId]
  );

  const detailTabIndex = tabIndexFromSearch(searchParams.get('tab'));

  function setDetailTabIndex(index: number) {
    const key = DETAIL_TAB_KEYS[index] ?? 'details';
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', key);
        return next;
      },
      { replace: true }
    );
  }

  if (!caseId || !investmentCase) {
    return (
      <ViewPage title="Dealflow" backTo="/investment-cases" backLabel="Back to dealflow">
        <section>
          <p className="muted">Case not found.</p>
        </section>
      </ViewPage>
    );
  }

  const c = investmentCase;
  const analyzing = isAnalyzing('deck', c.id);
  const analyzeError = getJobError('deck', c.id);
  const avg = investmentCaseAverageScore(c);
  const verdict = investmentCaseVerdict(c);
  const websiteHref = normalizeWebsiteUrl(c.websiteUrl);
  const flagUrl = flagSvgUrlForCountryCode(c.countryCode);

  let websiteHost: string | null = null;
  if (websiteHref) {
    try {
      websiteHost = new URL(websiteHref).hostname;
    } catch {
      websiteHost = null;
    }
  }

  function saveCase(next: InvestmentCase) {
    const id = next.id?.trim() ? next.id : newId();
    const merged = { ...next, id };
    setInvestmentCases((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      if (idx === -1) return [...prev, merged];
      const copy = [...prev];
      copy[idx] = merged;
      return copy;
    });
    setEditOpen(false);
  }

  async function deleteCase() {
    await deleteInvestmentCaseDeck(c.deckPdfStoragePath);
    setInvestmentCases((prev) => prev.filter((p) => p.id !== c.id));
    navigate('/investment-cases');
  }

  function runDeckAnalysis() {
    if (isAnalyzing('deck', c.id)) return;

    const ocrForAnalysis = c.deckOcrText?.trim() ?? '';
    if (ocrForAnalysis.length < 200) return;

    void startDeckAnalysis(c.id);
  }

  async function reExtractDeckText() {
    if (!c.deckPdfStoragePath || extracting) return;

    setExtracting(true);
    setExtractFeedback(null);

    try {
      const text = await extractDeckText(c.deckPdfStoragePath);
      const trimmed = text.trim();
      const next: InvestmentCase = {
        ...c,
        deckOcrText: trimmed || undefined,
        updatedAt: new Date().toISOString(),
      };
      setInvestmentCases((prev) => prev.map((p) => (p.id === c.id ? next : p)));
      setExtractFeedback({
        kind: 'success',
        message: trimmed
          ? `Extracted ${trimmed.length.toLocaleString()} characters from PDF.`
          : 'No embedded text found in the PDF.',
      });
    } catch (err) {
      setExtractFeedback({
        kind: 'error',
        message: extractDeckTextErrorMessage(err),
      });
    } finally {
      setExtracting(false);
    }
  }

  const detailsTab = (
    <section className="grid-stats grid-stats-2" aria-label="Case summary">
      <div className="stat-card">
        <div className="stat-label">Overall score</div>
        <div className="stat-value">
          {c.websiteUrl ? (
            <Favicon
              websiteUrl={c.websiteUrl}
              size={28}
              className="companyFavicon investmentCaseDetailStatFavicon"
              alt=""
            />
          ) : (
            <span className="stat-emoji" aria-hidden>
              ⭐
            </span>
          )}
          <span>{avg.toFixed(2)}</span>
        </div>
        <div className="stat-hint">Average of five 1–6 ratings</div>
      </div>

      <div
        className={`stat-card${verdict === 'INVEST' ? ' stat-card--return-positive' : ' stat-card--return-negative'}`}
      >
        <div className="stat-label">Verdict</div>
        <div className="stat-value">
          <span className="stat-emoji" aria-hidden>
            {verdict === 'INVEST' ? '✅' : '⛔'}
          </span>
          <span>{verdict}</span>
        </div>
        <div className="stat-hint">INVEST when average ≥ 4</div>
      </div>

      <div className="stat-card">
        <div className="stat-label">Industry</div>
        <div className="stat-value stat-value--wrap">
          <span className="stat-emoji" aria-hidden>
            🏭
          </span>
          <span>{c.industry || '—'}</span>
        </div>
      </div>

      <div className="stat-card">
        <div className="stat-label">Founding year</div>
        <div className="stat-value">
          <span className="stat-emoji" aria-hidden>
            📅
          </span>
          <span>{c.foundingYear ?? '—'}</span>
        </div>
      </div>

      {flagUrl ? (
        <div className="stat-card">
          <div className="stat-label">Country</div>
          <div className="stat-value">
            <img
              src={flagUrl}
              alt=""
              className="ticketCellFlag investmentCaseDetailStatFlag"
              width={24}
              height={20}
              loading="lazy"
              decoding="async"
            />
            <span>{c.countryCode?.toUpperCase()}</span>
          </div>
        </div>
      ) : null}

      <div className="stat-card investmentCaseScoreRadarCard">
        <InvestmentCaseScoreRadar investmentCase={c} />
      </div>
    </section>
  );

  const deckEmptyHint = 'Upload a PDF in Edit, or paste source text there.';
  const ocrText = c.deckOcrText?.trim() ?? '';
  const ocrIsLong = ocrText.length > 800;
  const hasOcrContent = ocrText.length > 0;
  const canAnalyze = ocrText.length >= 200;
  const deckIntelligence = c.deckIntelligence;
  const hasAnalysis = Boolean(deckIntelligence);
  const currentOcrFingerprint = hasOcrContent ? deckIntelligenceSourceFingerprint(ocrText) : '';
  const analysisIsStale =
    hasAnalysis &&
    hasOcrContent &&
    deckIntelligence!.sourceTextFingerprint !== currentOcrFingerprint;
  const intelligenceTabIndex = DETAIL_TAB_KEYS.indexOf('intelligence');

  const deckTab = (
    <section className="investmentCaseDetailDeck" aria-label="Pitch deck">
      <div className="investmentCaseDetailDeckFile">
        <p className="eyebrow">Pitch deck</p>
        {c.deckPdfUrl ? (
          <>
            <div className="investmentCaseDetailDeckFileRow">
              <a
                href={c.deckPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="investmentCaseDetailDeckFileName"
              >
                {c.deckPdfFileName || 'Open pitch deck'}
              </a>
              {c.deckPdfStoragePath ? (
                <GlobalButton
                  type="button"
                  variant="secondary"
                  disabled={extracting}
                  onClick={() => void reExtractDeckText()}
                >
                  {extracting ? 'Extracting…' : 'Re-extract text'}
                </GlobalButton>
              ) : null}
            </div>
            {extractFeedback ? (
              <p
                className={
                  extractFeedback.kind === 'error'
                    ? 'sidebarFormError investmentCaseDetailDeckFeedback'
                    : 'muted investmentCaseDetailDeckFeedback'
                }
                role={extractFeedback.kind === 'error' ? 'alert' : 'status'}
              >
                {extractFeedback.message}
              </p>
            ) : null}
          </>
        ) : (
          <p className="muted">{deckEmptyHint}</p>
        )}
      </div>

      {hasOcrContent ? (
        <div className="investmentCaseDetailDeckOcr">
          <GlobalCollapsibleButton
            title={`Source text · ${ocrText.length.toLocaleString()} characters`}
            isOpen={!ocrIsLong}
          >
            <pre className="monthlyReportRaw investmentCaseDetailDeckOcrPre">{ocrText}</pre>
          </GlobalCollapsibleButton>
        </div>
      ) : null}

      <div className="investmentCaseDetailDeckAnalyze">
        <p className="eyebrow">Analysis</p>
        {!hasOcrContent ? (
          <p className="muted">{deckEmptyHint}</p>
        ) : (
          <>
            {hasAnalysis ? (
              <p className="muted investmentCaseDetailDeckAnalysisMeta">
                Analyzed {new Date(deckIntelligence!.analyzedAt).toLocaleString()} ·{' '}
                {deckIntelligence!.model}
              </p>
            ) : null}
            {!canAnalyze ? (
              <p className="muted">
                Source text must be at least 200 characters before analyzing (currently{' '}
                {ocrText.length.toLocaleString()}).
              </p>
            ) : null}
            <div className="investmentCaseDetailAnalyzeActions">
              {!hasAnalysis ? (
                <GlobalButton
                  type="button"
                  variant="primary"
                  disabled={!canAnalyze || analyzing}
                  onClick={() => runDeckAnalysis()}
                >
                  {analyzing ? 'Analyzing…' : 'Analyze deck'}
                </GlobalButton>
              ) : (
                <>
                  <GlobalButton
                    type="button"
                    variant="secondary"
                    disabled={!canAnalyze || analyzing}
                    onClick={() => runDeckAnalysis()}
                  >
                    {analyzing ? 'Analyzing…' : 'Regenerate'}
                  </GlobalButton>
                  {!analysisIsStale ? (
                    <GlobalButton
                      type="button"
                      variant="secondary"
                      onClick={() => setDetailTabIndex(intelligenceTabIndex)}
                    >
                      View intelligence
                    </GlobalButton>
                  ) : null}
                </>
              )}
              {analysisIsStale ? (
                <span className="verdictPill verdictPill--dont investmentCaseDetailStalePill">
                  Source text changed — re-analyze
                </span>
              ) : null}
            </div>
            {analyzeError ? (
              <div className="investmentCaseDetailAnalyzeError">
                <p className="sidebarFormError" role="alert">
                  {analyzeError}
                </p>
                <GlobalButton
                  type="button"
                  variant="secondary"
                  disabled={!canAnalyze || analyzing}
                  onClick={() => runDeckAnalysis()}
                >
                  Retry analysis
                </GlobalButton>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );

  const intelligenceTab = (
    <section className="investmentCaseDetailIntelligence" aria-label="Deck intelligence">
      {!hasOcrContent ? (
        <GlobalEmptyState
          buttonText="Edit deck"
          onButtonClick={() => setEditOpen(true)}
        />
      ) : !hasAnalysis ? (
        <div className="investmentCaseDetailIntelligenceEmpty">
          {!canAnalyze ? (
            <p className="muted investmentCaseDetailIntelligenceEmptyHint">
              Deck OCR text must be at least 200 characters before analyzing (currently{' '}
              {ocrText.length.toLocaleString()}). Upload a PDF or paste more text in Edit.
            </p>
          ) : null}
          <GlobalEmptyState
            buttonText={
              analyzing
                ? 'Analyzing…'
                : canAnalyze
                  ? 'Analyze deck'
                  : 'Edit deck'
            }
            onButtonClick={() => {
              if (canAnalyze && !analyzing) {
                runDeckAnalysis();
              } else if (!canAnalyze) {
                setEditOpen(true);
              }
            }}
          />
          {analyzeError ? (
            <div className="investmentCaseDetailAnalyzeError">
              <p className="sidebarFormError" role="alert">
                {analyzeError}
              </p>
              <GlobalButton
                type="button"
                variant="secondary"
                disabled={!canAnalyze || analyzing}
                onClick={() => runDeckAnalysis()}
              >
                Retry analysis
              </GlobalButton>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="investmentCaseDetailIntelligenceScaffold">
            <DeckIntelligenceSections intelligence={deckIntelligence!} />
            <section aria-label="Synthesis" className="investmentCaseDetailIntelligenceSection" />
            <section
              aria-label="Historically similar companies"
              className="investmentCaseDetailIntelligenceSection"
            />
          </div>
        </>
      )}
    </section>
  );

  const founders = c.founders ?? [];

  const foundersTab = (
    <section className="investmentCaseDetailFounders" aria-label="Founders">
      {founders.length === 0 ? (
        <div className="empty-inline">No founders yet — add them when editing the case.</div>
      ) : (
        <ul className="investmentCaseDetailFounderList">
          {founders.map((founder, index) => (
            <li key={founder.id} className="investmentCaseFounderCard">
              <p className="investmentCaseFounderCardTitle">
                {founder.name?.trim() || `Founder ${index + 1}`}
              </p>
              {founder.linkedinUrl ? (
                <p>
                  <a href={founder.linkedinUrl} target="_blank" rel="noopener noreferrer">
                    {founder.linkedinUrl}
                  </a>
                </p>
              ) : (
                <p className="muted">No LinkedIn URL</p>
              )}
              {founder.profileText ? (
                <pre className="monthlyReportRaw investmentCaseDetailFounderBio">{founder.profileText}</pre>
              ) : (
                <p className="muted">No profile text</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  const chatTab = (
    <>
      <ViewSectionHeader
        eyebrow="Chat"
        title="Talk it through"
        subtitle="Ask questions about this deal — grounded in the deck, DD scores, intelligence analysis, and your Perfina data. Conversations are saved as separate threads."
      />
      <InvestmentCaseChatPanel userId={userId} caseId={c.id} caseName={c.name} />
    </>
  );

  const detailActions = (
    <>
      <GlobalButton
        type="button"
        variant="secondary"
        onClick={() => setEditOpen(true)}
        icon={
          <img
            src={isDarkMode ? '/assets/icons/pencil.svg' : '/assets/icons/pencil.black.svg'}
            alt=""
            width={16}
            height={16}
          />
        }
      >
        Edit case
      </GlobalButton>
      <GlobalButton
        type="button"
        variant="secondary"
        destructive
        onClick={() => void deleteCase()}
        icon={<img src="/assets/icons/trash.red.svg" alt="" width={16} height={16} />}
      >
        Delete case
      </GlobalButton>
    </>
  );

  return (
    <ViewPage
      title={c.name}
      backTo="/investment-cases"
      backLabel="Back to dealflow"
      actions={detailActions}
    >
      <ViewSectionHeader
        eyebrow="Dealflow"
        title={c.name}
        subtitle={c.industry || '—'}
      >
        {websiteHref && websiteHost ? (
          <p style={{ marginTop: 8 }}>
            <a
              href={websiteHref}
              target="_blank"
              rel="noopener noreferrer"
              className="detailWebsiteLink"
            >
              {websiteHost}
            </a>
          </p>
        ) : null}
      </ViewSectionHeader>

      <section>
        <GlobalTabs
        initialIndex={detailTabIndex}
        onChange={setDetailTabIndex}
        tabs={[
          { label: 'Details', content: detailsTab },
          { label: 'Deck', content: deckTab },
          { label: 'Founders', content: foundersTab },
          { label: 'Intelligence', content: intelligenceTab },
          { label: 'Chat', content: chatTab },
        ]}
        />
      </section>

      <GlobalSidebar isOpen={editOpen} onClose={() => setEditOpen(false)}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Edit case</h2>
          <InvestmentCaseForm
            investmentCase={c}
            userId={userId}
            onSubmit={saveCase}
            onCancel={() => setEditOpen(false)}
            onDelete={() => void deleteCase()}
          />
        </div>
      </GlobalSidebar>
    </ViewPage>
  );
}
